import { useCallback, useEffect, useRef, useState, type TouchEvent, type TouchList } from 'react';
import { createPortal } from 'react-dom';
import type { AttachmentDto } from '@initiative/shared';
import { FotoWerkstatt } from './FotoWerkstatt.js';
import { CastKnopf } from '../fernseher/CastKnopf.js';
import { DateiAktionen } from './DateiAktionen.js';
import { mediaDownloadSrc, mediaSrc } from './helpers.js';
import { useDialogAnmeldung } from '../../lib/dialogAnmeldung.js';

interface LightboxProps {
  items: AttachmentDto[];
  index: number;
  onClose: () => void;
  /**
   * Wohin eine bearbeitete Fassung gehört. Ohne das bleibt vom Bearbeiten nur
   * das Speichern aufs Telefon.
   */
  ablegen?: (blob: Blob, name: string) => Promise<void>;
  alsRezept?: (original: Blob, rezept: Blob, name: string) => Promise<void>;
  zielName?: string;
}

type GestureMode = 'none' | 'pan' | 'pinch' | 'dismiss';

interface GestureState {
  mode: GestureMode;
  startDistance: number;
  startScale: number;
  startX: number;
  startY: number;
  startOffsetX: number;
  startOffsetY: number;
}

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DISMISS_DISTANCE = 120;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function distanceBetween(touches: TouchList): number {
  const [a, b] = [touches[0], touches[1]];
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/**
 * Full screen photo viewer: pinch and double tap to zoom, drag to pan, swipe
 * down to close. `touch-action: none` keeps Safari from hijacking the gesture.
 */
export function Lightbox({ items, index, onClose, ablegen, alsRezept, zielName }: LightboxProps) {
  const [current, setCurrent] = useState(index);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dismissY, setDismissY] = useState(0);
  /**
   * Solange Editor oder Studio offen sind, hört der Betrachter auf, Tasten zu
   * deuten – sonst schlösse Escape beides auf einmal und die Pfeiltasten
   * blätterten hinter dem Editor weiter.
   */
  const [werkstattOffen, setWerkstattOffen] = useState(false);
  const [aktionenOffen, setAktionenOffen] = useState(false);
  const gesture = useRef<GestureState>({
    mode: 'none',
    startDistance: 0,
    startScale: 1,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });
  const moved = useRef(false);
  const lastTap = useRef(0);
  const letzteBeruehrung = useRef(0);

  /*
   * Die Zurück-Taste schliesst den Betrachter, statt aus dem Chat zu springen.
   *
   * Jedes andere Overlay der App macht das (`Sheet`, der Fotoeditor) – dieses
   * nicht, und der Unterschied fiel niemandem auf, der es gebaut hat: Auf
   * einem Rechner gibt es keine Zurück-Geste. Auf dem Telefon ist sie die
   * naheliegendste Art, ein Vollbild zu verlassen.
   */
  useDialogAnmeldung(true, onClose);
  const item = items[current];

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setDismissY(0);
  }, []);

  const go = useCallback(
    (delta: number) => {
      if (items.length < 2) return;
      setCurrent((value) => (value + delta + items.length) % items.length);
      reset();
    },
    [items.length, reset],
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    /*
     * Nur die PFEILE. Esc liegt bei `dialogVerlauf.ts`, wo die Buchführung
     * darüber steht, welcher Dialog oben liegt – die Lightbox meldet sich
     * dort an. Ein eigener Esc-Hörer hier hätte bei einem offenen Blatt
     * darüber beide auf einmal geschlossen.
     */
    if (werkstattOffen || aktionenOffen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go, werkstattOffen, aktionenOffen]);

  const toggleZoom = useCallback(() => {
    setScale((value) => (value > 1 ? 1 : DOUBLE_TAP_SCALE));
    setOffset({ x: 0, y: 0 });
  }, []);

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    moved.current = false;
    if (event.touches.length >= 2) {
      gesture.current = {
        mode: 'pinch',
        startDistance: distanceBetween(event.touches),
        startScale: scale,
        startX: 0,
        startY: 0,
        startOffsetX: offset.x,
        startOffsetY: offset.y,
      };
      return;
    }
    const touch = event.touches[0];
    gesture.current = {
      mode: scale > 1 ? 'pan' : 'dismiss',
      startDistance: 0,
      startScale: scale,
      startX: touch.clientX,
      startY: touch.clientY,
      startOffsetX: offset.x,
      startOffsetY: offset.y,
    };
  };

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const state = gesture.current;
    if (state.mode === 'pinch' && event.touches.length >= 2) {
      const factor = distanceBetween(event.touches) / (state.startDistance || 1);
      const next = clamp(state.startScale * factor, 1, MAX_SCALE);
      moved.current = true;
      setScale(next);
      if (next <= 1.01) setOffset({ x: 0, y: 0 });
      return;
    }
    const touch = event.touches[0];
    if (!touch) return;
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;
    if (Math.hypot(dx, dy) > 8) moved.current = true;
    if (state.mode === 'pan') {
      setOffset({ x: state.startOffsetX + dx, y: state.startOffsetY + dy });
    } else if (state.mode === 'dismiss') {
      setDismissY(Math.max(0, dy));
    }
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length > 0) return;
    const state = gesture.current;
    gesture.current = { ...state, mode: 'none' };
    if (state.mode === 'dismiss' && dismissY > DISMISS_DISTANCE) {
      onClose();
      return;
    }
    setDismissY(0);
    // Merkt, dass gerade ein Finger im Spiel war – siehe `beiDoppelklick`.
    letzteBeruehrung.current = Date.now();
    if (!moved.current) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        toggleZoom();
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    }
  };

  /**
   * Doppelklick – aber nur mit der Maus.
   *
   * Ein Doppeltipp löste bisher ZWEIMAL aus: einmal über die eigene Erkennung
   * in `onTouchEnd` und danach noch einmal über den `dblclick`, den der
   * Browser aus denselben Berührungen erzeugt. Zweimal umschalten heisst nicht
   * umschalten – der Hinweis „Doppeltippen zum Zoomen" versprach also etwas,
   * das auf jedem Telefon wirkungslos war.
   */
  const beiDoppelklick = () => {
    if (Date.now() - letzteBeruehrung.current < 800) return;
    toggleZoom();
  };

  if (!item) return null;

  const fadeOut = Math.min(dismissY / 400, 0.7);

  return createPortal(
    <div
      className="media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Foto"
      /*
       * Solange Editor oder Sticker-Studio offen sind, ist der Betrachter
       * nicht bedienbar – auch nicht für einen Vorleser. `inert` und nicht
       * `aria-hidden`: Der Knopf, über den man gerade in den Editor gegangen
       * ist, hat den Tastaturfokus, und `aria-hidden` über einem
       * fokussierten Element ist ein Fehler. `inert` nimmt ihn korrekt weg.
       *
       * Die Anzeige regelt das nicht: Editor und Studio liegen jetzt mit
       * z-index 75 darüber. Das hier ist die Ebene darunter.
       */
      inert={werkstattOffen}
    >
      <div className="media-lightbox-bar" style={{ opacity: 1 - fadeOut }}>
        <button type="button" className="media-round-btn" onClick={onClose} aria-label="Schließen">
          ✕
        </button>
        {items.length > 1 && (
          <span className="media-lightbox-count">
            {current + 1} / {items.length}
          </span>
        )}
        <div className="media-lightbox-tools">
          {/*
            Der Cast-Knopf steht auf OBERSTER Ebene und nicht in einem
            Klappmenü – das verlangen die Nutzungsbedingungen des Cast-SDK
            (§5.1), und zwar auf jeder Seite mit castbarem Inhalt. Die
            Begründung im Ganzen steht in `fernseher/cast.ts`.
          */}
          <CastKnopf stuecke={[item.id]} was={item.fileName ?? 'Dieses Foto'} />
          <FotoWerkstatt
            foto={item}
            ablegen={ablegen}
            alsRezept={alsRezept}
            zielName={zielName}
            onOffen={setWerkstattOffen}
          />
          <a
            className="media-round-btn"
            href={mediaDownloadSrc(item)}
            target="_blank"
            rel="noreferrer"
            aria-label="Herunterladen"
          >
            ⬇
          </a>
          {/*
            Weitergeben und Priorität – aber kein Löschen.

            Ein Foto im Chat hängt an einer Nachricht und geht mit ihr; das
            steht schon im Nachrichtenmenü, mit der Unterscheidung „für mich"
            und „für alle", die es hier gar nicht gäbe. Ein zweiter
            Löschknopf mit einer dritten Bedeutung wäre die schlechtere Art,
            vollständig zu sein.
          */}
          <button
            type="button"
            className="media-round-btn"
            aria-label="Mehr"
            title="Teilen, Priorität"
            onClick={() => setAktionenOffen(true)}
          >
            ⋯
          </button>
        </div>
      </div>

      <DateiAktionen
        open={aktionenOffen}
        onClose={() => setAktionenOffen(false)}
        anhaenge={[item]}
      />

      <div
        className="media-zoom"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onDoubleClick={beiDoppelklick}
      >
        <img
          src={mediaSrc(item)}
          alt={item.fileName ?? 'Foto'}
          className="media-zoom-image"
          draggable={false}
          style={{
            transform: `translate3d(${offset.x}px, ${offset.y + dismissY}px, 0) scale(${scale})`,
            opacity: 1 - fadeOut,
            transition: gesture.current.mode === 'none' ? 'transform 160ms ease-out' : 'none',
          }}
        />
      </div>

      {items.length > 1 && (
        <div className="media-lightbox-nav">
          <button
            type="button"
            className="media-round-btn"
            onClick={() => go(-1)}
            aria-label="Vorheriges Foto"
          >
            ‹
          </button>
          <button
            type="button"
            className="media-round-btn"
            onClick={() => go(1)}
            aria-label="Nächstes Foto"
          >
            ›
          </button>
        </div>
      )}
      <p className="media-lightbox-hint">
        Nach unten wischen zum Schließen · Doppeltippen zum Zoomen
      </p>
    </div>,
    document.body,
  );
}
